import React, { useState, useCallback, useRef } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { uploadVideoToMatchREST } from "./RESTUpload";
import {
  Box,
  LinearProgress,
  Typography as MuiTypography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import Typography from "../typography";

// Constants for video validation
const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes
const ACCEPTED_VIDEO_FORMATS = [
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
  "video/webm",
  "video/3gpp",
  "video/x-flv",
];

const ACCEPTED_VIDEO_EXTENSIONS = [
  ".mp4",
  ".mpeg",
  ".mov",
  ".avi",
  ".wmv",
  ".webm",
  ".3gp",
  ".flv",
];

interface VideoUploaderProps {
  matchId: string;
  videosCount: number;
  maxVideos?: number;
  onUploadSuccess?: () => void;
  onUploadProcessing?: (fileName: string) => string;
}

interface UploadState {
  status: "idle" | "uploading" | "processing" | "success" | "error";
  progress: number;
  error: string | null;
  fileName: string | null;
  fileSize: number | null;
}

interface PendingVideo {
  file: File;
  title: string;
}

// Styled Components
const UploaderContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.default,
  borderRadius: "12px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const DropZone = styled("div")(({ theme }: IStyledArguments) => ({
  border: `2px dashed ${theme.palette.grey[400]}`,
  borderRadius: "12px",
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  background: theme.palette.extra.backgroundGrey,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    background: `${theme.palette.primary.main}05`,
  },
  "&[data-disabled='true']": {
    cursor: "not-allowed",
    borderColor: theme.palette.grey[300],
    background: theme.palette.grey[100],
    "&:hover": {
      borderColor: theme.palette.grey[300],
      background: theme.palette.grey[100],
    },
  },
  "&[data-dragging='true']": {
    borderColor: theme.palette.primary.main,
    background: `${theme.palette.primary.main}10`,
  },
}));

const UploadIcon = styled(CloudUploadIcon)(({ theme }: IStyledArguments) => ({
  fontSize: "4rem",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
}));

const FilePreview = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "8px",
  marginTop: theme.spacing(2),
}));

const FileInfo = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flex: 1,
});

const StatusIcon = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: theme.palette.primary.main,
}));

const ProgressContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  width: "100%",
  marginTop: theme.spacing(2),
}));

const ErrorMessage = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: "#fff3f3",
  borderRadius: "8px",
  color: "#d32f2f",
  marginTop: theme.spacing(2),
}));

const SuccessMessage = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: "#f0fdf4",
  borderRadius: "8px",
  color: "#16a34a",
  marginTop: theme.spacing(2),
}));

const ProcessingMessage = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: "#fef3c7",
  borderRadius: "8px",
  color: "#d97706",
  marginTop: theme.spacing(2),
}));

/**
 * Formats file size to human readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Validates video file format and size with enhanced iOS MOV support
 */
const validateVideoFile = (file: File): { valid: boolean; error: string | null } => {
  console.log(`📹 Validating file:`, {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified
  });

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File is too large (max 500MB). Your file is ${formatFileSize(file.size)}`,
    };
  }

  // Check for empty files (common issue with iOS MOV files)
  if (file.size === 0) {
    return {
      valid: false,
      error: `File appears to be empty. Please try selecting the video again.`,
    };
  }

  // Enhanced file type validation for iOS
  const isValidType = ACCEPTED_VIDEO_FORMATS.includes(file.type);
  const isValidExtension = ACCEPTED_VIDEO_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );

  // Special handling for iOS MOV files that might have empty or incorrect MIME types
  const isLikelyIOSVideo = file.name.toLowerCase().includes('img_') && 
                          file.name.toLowerCase().endsWith('.mov');
  
  // Accept if any of these conditions are true:
  // 1. Valid MIME type
  // 2. Valid extension
  // 3. Looks like an iOS video file
  if (!isValidType && !isValidExtension && !isLikelyIOSVideo) {
    return {
      valid: false,
      error: `Unsupported format. Accepted formats: ${ACCEPTED_VIDEO_EXTENSIONS.join(", ")}`,
    };
  }

  // Additional validation for suspicious files
  if (file.size < 1024) { // Less than 1KB is suspicious for a video
    return {
      valid: false,
      error: `File seems too small to be a valid video (${formatFileSize(file.size)}). Please try again.`,
    };
  }

  console.log(`✅ File validation passed:`, {
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type || 'unknown'
  });

  return { valid: true, error: null };
};

const VideoUploader: React.FC<VideoUploaderProps> = ({
  matchId,
  videosCount,
  maxVideos = 20,
  onUploadSuccess,
  onUploadProcessing,
}) => {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: "idle",
    progress: 0,
    error: null,
    fileName: null,
    fileSize: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [pendingVideo, setPendingVideo] = useState<PendingVideo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDisabled = videosCount >= maxVideos || uploadState.status === "uploading" || uploadState.status === "processing";

  /**
   * Handles file selection and shows modal for title input
   */
  const handleFileSelected = useCallback(
    (file: File) => {
      // Validate file
      const validation = validateVideoFile(file);
      if (!validation.valid) {
        setUploadState({
          status: "error",
          progress: 0,
          error: validation.error,
          fileName: file.name,
          fileSize: file.size,
        });
        return;
      }

      // Check video limit
      if (videosCount >= maxVideos) {
        setUploadState({
          status: "error",
          progress: 0,
          error: `Maximum limit of ${maxVideos} videos reached for this match`,
          fileName: file.name,
          fileSize: file.size,
        });
        return;
      }

      // Generate default title from filename
      const defaultTitle = file.name.replace(/\.[^/.]+$/, "");
      
      // Set pending video and open modal
      setPendingVideo({
        file,
        title: defaultTitle,
      });
      setIsModalOpen(true);
    },
    [videosCount, maxVideos]
  );

  /**
   * Handles actual video upload after user confirms in modal
   */
  const handleUpload = useCallback(
    async () => {
      if (!pendingVideo) return;

      const { file, title } = pendingVideo;

      // Close modal and start upload
      setIsModalOpen(false);
      setUploadState({
        status: "uploading",
        progress: 0,
        error: null,
        fileName: file.name,
        fileSize: file.size,
      });

      console.log(`🚀 Starting upload for file:`, {
        name: file.name,
        type: file.type,
        size: formatFileSize(file.size),
        lastModified: new Date(file.lastModified).toISOString()
      });

      try {
        // Ensure the file is a proper File object for apollo-upload-client
        console.log(`📦 File object details:`, {
          isFile: file instanceof File,
          isBlob: file instanceof Blob,
          constructor: file.constructor.name,
          keys: Object.keys(file),
          name: file.name,
          type: file.type,
          size: file.size
        });
        
        // Log the file details being sent to REST upload
        console.log(`📤 File being sent to REST upload:`, {
          matchId,
          fileTypeof: typeof file,
          fileConstructor: file.constructor.name,
          fileIsFile: file instanceof File,
          fileName: file.name,
          fileSize: file.size,
          fileMimeType: file.type
        });
        
        // Use REST upload with progress tracking
        const result = await uploadVideoToMatchREST(
          matchId, 
          file, 
          (progress) => {
            setUploadState((prev) => ({ ...prev, progress }));
          },
          title || undefined
        );

        console.log(`✅ REST upload successful:`, result);

        if (result?.success && result?.data?.video) {
          // File uploaded to server successfully - now add processing placeholder
          if (onUploadProcessing) {
            onUploadProcessing(file.name);
          }
          
          // Show success state immediately (file uploaded to server)
          setUploadState({
            status: "success",
            progress: 100,
            error: null,
            fileName: file.name,
            fileSize: file.size,
          });

          // Reset uploader state after showing success
          setTimeout(() => {
            setUploadState({
              status: "idle",
              progress: 0,
              error: null,
              fileName: null,
              fileSize: null,
            });
            setPendingVideo(null); // Clear the pending video
          }, 2000);
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        
        let errorMessage = "Error uploading video. Please try again.";
        
        // Enhanced error handling for REST API
        if (error.message?.includes("No hay token")) {
          errorMessage = "Session expired. Please log in again.";
        } else if (error.message?.includes("401") || error.message?.includes("Unauthorized")) {
          errorMessage = "You don't have permission to upload videos to this match.";
        } else if (error.message?.includes("404") || error.message?.includes("no encontrado")) {
          errorMessage = "The match doesn't exist or you don't have access to it.";
        } else if (error.message?.includes("413") || error.message?.includes("too large")) {
          errorMessage = "File is too large. Maximum allowed: 500MB.";
        } else if (error.message?.includes("415") || error.message?.includes("Unsupported")) {
          errorMessage = "Unsupported video format. Use MP4, MOV, M4V, AVI, etc.";
        } else if (error.message?.includes("conexión") || error.message?.includes("Network")) {
          errorMessage = "Connection error. Check your internet connection and server URL.";
        } else if (error.message?.includes("timeout") || error.message?.includes("agotado")) {
          errorMessage = "Upload timeout. File may be too large or connection too slow.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setUploadState({
          status: "error",
          progress: 0,
          error: errorMessage,
          fileName: file.name,
          fileSize: file.size,
        });
      }
    },
    [matchId, onUploadSuccess, pendingVideo]
  );

  /**
   * Handles file selection from input
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
    // Reset input value to allow same file selection
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handles drag and drop events
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (isDisabled) return;

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  /**
   * Opens file picker
   */
  const handleClick = () => {
    if (!isDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * Clears current upload state
   */
  const handleClear = () => {
    setUploadState({
      status: "idle",
      progress: 0,
      error: null,
      fileName: null,
      fileSize: null,
    });
  };

  return (
    <UploaderContainer>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: "8px" }}>
        <VideoLibraryIcon color="primary" />
        <Typography.H6 style={{ margin: 0 }}>Upload Match Video</Typography.H6>
        <Box ml="auto">
          <Typography.Body style={{ color: "#666", fontSize: "0.9rem" }}>
            {videosCount}/{maxVideos} videos
          </Typography.Body>
        </Box>
      </Box>

      <DropZone
        data-dragging={isDragging.toString()}
        data-disabled={isDisabled.toString()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,.mp4,.mov,.avi,.webm,.m4v,.3gp,.flv,.wmv,.mpeg"
          style={{ display: "none" }}
          onChange={handleFileSelect}
          disabled={isDisabled}
        />

        <UploadIcon style={{ opacity: isDisabled ? 0.3 : 1 }} />
        
        <MuiTypography variant="h6" style={{ marginBottom: "8px" }}>
          {isDisabled && videosCount >= maxVideos
            ? `Maximum ${maxVideos} videos reached`
            : "Drag & drop video here"}
        </MuiTypography>
        
        <MuiTypography variant="body2" color="textSecondary">
          {!isDisabled && "or click to browse"}
        </MuiTypography>
        
        <MuiTypography
          variant="caption"
          color="textSecondary"
          style={{ display: "block", marginTop: "12px" }}
        >
          Max 500MB • MP4, MOV, AVI, WebM, and more
        </MuiTypography>
      </DropZone>

      {/* File Preview & Status */}
      {uploadState.fileName && (
        <FilePreview>
          <FileInfo>
            <StatusIcon
              style={{
                background:
                  uploadState.status === "error"
                    ? "#d32f2f"
                    : uploadState.status === "success"
                    ? "#16a34a"
                    : uploadState.status === "processing"
                    ? "#d97706"
                    : "#1976d2",
              }}
            >
              {uploadState.status === "error" ? (
                <ErrorIcon style={{ color: "white" }} />
              ) : uploadState.status === "success" ? (
                <CheckCircleIcon style={{ color: "white" }} />
              ) : uploadState.status === "processing" ? (
                <HourglassEmptyIcon style={{ color: "white" }} />
              ) : (
                <VideoLibraryIcon style={{ color: "white" }} />
              )}
            </StatusIcon>
            <Box>
              <MuiTypography variant="body2" style={{ fontWeight: 600 }}>
                {uploadState.fileName}
              </MuiTypography>
              <MuiTypography variant="caption" color="textSecondary">
                {uploadState.fileSize && formatFileSize(uploadState.fileSize)}
              </MuiTypography>
            </Box>
          </FileInfo>
          
          {uploadState.status !== "uploading" && uploadState.status !== "processing" && (
            <IconButton size="small" onClick={handleClear}>
              <CloseIcon />
            </IconButton>
          )}
        </FilePreview>
      )}

      {/* Progress Bar */}
      {uploadState.status === "uploading" && (
        <ProgressContainer>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <MuiTypography variant="body2">Uploading to YouTube...</MuiTypography>
            <MuiTypography variant="body2">{uploadState.progress}%</MuiTypography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={uploadState.progress}
            style={{ height: "8px", borderRadius: "4px" }}
          />
        </ProgressContainer>
      )}

      {/* Error Message */}
      {uploadState.status === "error" && uploadState.error && (
        <ErrorMessage>
          <ErrorIcon />
          <MuiTypography variant="body2">{uploadState.error}</MuiTypography>
        </ErrorMessage>
      )}

      {/* Processing Message */}
      {uploadState.status === "processing" && (
        <ProcessingMessage>
          <HourglassEmptyIcon />
          <MuiTypography variant="body2">
            Video uploaded successfully! Processing... This may take a few moments.
          </MuiTypography>
        </ProcessingMessage>
      )}

      {/* Success Message */}
      {uploadState.status === "success" && (
        <SuccessMessage>
          <CheckCircleIcon />
          <MuiTypography variant="body2">
            Video uploaded to server successfully!
          </MuiTypography>
        </SuccessMessage>
      )}

      {/* Confirmation Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
            <VideoLibraryIcon color="primary" />
            <span>Upload Video</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <MuiTypography variant="body2" color="textSecondary" gutterBottom>
              Selected file:
            </MuiTypography>
            <MuiTypography variant="body1" style={{ fontWeight: 600 }}>
              {pendingVideo?.file.name}
            </MuiTypography>
            <MuiTypography variant="caption" color="textSecondary">
              {pendingVideo?.file.size && formatFileSize(pendingVideo.file.size)}
            </MuiTypography>
          </Box>
          
          <TextField
            fullWidth
            variant="outlined"
            label="Video Title"
            placeholder="e.g., Micah Goal 2-1"
            value={pendingVideo?.title || ""}
            onChange={(e) =>
              setPendingVideo((prev) =>
                prev ? { ...prev, title: e.target.value } : null
              )
            }
            helperText="Give your video a descriptive title"
            autoFocus
          />
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            onClick={() => {
              setIsModalOpen(false);
              setPendingVideo(null);
            }}
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            startIcon={<CloudUploadIcon />}
          >
            Upload Video
          </Button>
        </DialogActions>
      </Dialog>
    </UploaderContainer>
  );
};

export default VideoUploader;
